import { api, type SearchParams, type CarCardData, type SearchResult } from "@/lib/api";
import { Sentry } from "@/lib/sentry";

export type FallbackReason = "timeout" | "error" | "rate_limited";

type Listener = (active: boolean, reason: FallbackReason | null) => void;

const ALGOLIA_TIMEOUT_MS = 5000;
const FALLBACK_AUTO_CLEAR_MS = 60_000;

let _active = false;
let _reason: FallbackReason | null = null;
let _reportedThisSession = false;
let _autoClearTimer: ReturnType<typeof setTimeout> | null = null;
const _listeners = new Set<Listener>();

export function getFallbackState(): { active: boolean; reason: FallbackReason | null } {
  return { active: _active, reason: _reason };
}

export function subscribeFallback(cb: Listener): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function emit() {
  for (const l of _listeners) {
    try { l(_active, _reason); } catch { /* noop */ }
  }
}

function activateFallback(reason: FallbackReason, err: unknown, status: number | null) {
  _active = true;
  _reason = reason;

  if (_autoClearTimer) clearTimeout(_autoClearTimer);
  _autoClearTimer = setTimeout(() => {
    _active = false;
    _reason = null;
    emit();
  }, FALLBACK_AUTO_CLEAR_MS);

  emit();

  if (!_reportedThisSession) {
    _reportedThisSession = true;
    const message = err instanceof Error ? err.message : String(err);
    // Task #5064 — engaging the database fallback after an Algolia
    // timeout/rate-limit (Sentry DRIVESHARE_V2-4F) is a SUCCESSFUL
    // graceful-degradation path: the user still gets results from the
    // database. It used to `Sentry.captureMessage(... "warning")`, which
    // creates a recurring Sentry issue even though nothing is broken. We
    // keep the analytics event + console warning so the fallback RATE stays
    // observable, but no longer page Sentry on the happy path. A genuine
    // HARD failure (the database fallback itself throwing) is still reported
    // via `Sentry.captureException` inside `runSupabaseFallback`.
    void import("@/lib/analytics").then(({ track }) => {
      try {
        track("search_fallback_used", { reason, status, message });
      } catch { /* noop */ }
    });
    if (typeof console !== "undefined") {
      console.warn(`[search] Algolia fallback engaged (${reason}):`, message);
    }
  }
}

export function clearFallback() {
  if (!_active) return;
  if (_autoClearTimer) {
    clearTimeout(_autoClearTimer);
    _autoClearTimer = null;
  }
  _active = false;
  _reason = null;
  emit();
}

export async function withTimeout<T>(p: Promise<T>, ms = ALGOLIA_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      p,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          const err = new Error(`Algolia request timed out after ${ms}ms`);
          (err as Error & { code?: string }).code = "ETIMEDOUT";
          reject(err);
        }, ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

type AlgoliaRequest = {
  indexName?: string;
  query?: string;
  params?: Record<string, unknown> | string;
  facetFilters?: unknown;
  numericFilters?: unknown;
  filters?: string;
  page?: number;
  hitsPerPage?: number;
  aroundLatLng?: string;
  aroundRadius?: number | string;
  insideBoundingBox?: number[][] | string;
};

function parseQs(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of s.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    const k = decodeURIComponent(eq === -1 ? part : part.slice(0, eq));
    const v = eq === -1 ? "" : decodeURIComponent(part.slice(eq + 1));
    out[k] = v;
  }
  return out;
}

function flattenFacetFilters(input: unknown): string[] {
  if (!input) return [];
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      return flattenFacetFilters(parsed);
    } catch {
      return [input];
    }
  }
  if (Array.isArray(input)) {
    const out: string[] = [];
    for (const item of input) {
      if (typeof item === "string") out.push(item);
      else if (Array.isArray(item)) {
        for (const sub of item) {
          if (typeof sub === "string") out.push(sub);
        }
      }
    }
    return out;
  }
  return [];
}

function flattenNumeric(input: unknown): string[] {
  if (!input) return [];
  if (typeof input === "string") {
    try { return flattenNumeric(JSON.parse(input)); } catch { return [input]; }
  }
  if (Array.isArray(input)) return input.flat().filter((v): v is string => typeof v === "string");
  return [];
}

const SORT_INDEX_TO_KEY: Record<string, string> = {
  price_asc: "price_asc",
  price_desc: "price_desc",
  newest: "newest",
  top_rated: "top_rated",
};

export function algoliaRequestToSearchParams(req: AlgoliaRequest): SearchParams {
  const params: SearchParams = {};

  // Merge `params` (which may be a query string or an object) with top-level fields
  let merged: Record<string, unknown> = {};
  if (typeof req.params === "string") merged = parseQs(req.params);
  else if (req.params && typeof req.params === "object") merged = { ...(req.params as Record<string, unknown>) };
  for (const k of ["query", "facetFilters", "numericFilters", "filters", "page", "hitsPerPage", "aroundLatLng", "aroundRadius", "insideBoundingBox"] as const) {
    if (req[k] != null && merged[k] == null) merged[k] = req[k];
  }

  const query = (merged.query as string) || "";
  if (query) params.q = query;

  const page = Number(merged.page);
  if (Number.isFinite(page)) params.page = page + 1; // Algolia is 0-indexed
  const hitsPerPage = Number(merged.hitsPerPage);
  if (Number.isFinite(hitsPerPage) && hitsPerPage > 0) params.pageSize = hitsPerPage;
  if (typeof merged.date_from === "string") params.date_from = merged.date_from;
  if (typeof merged.date_to === "string") params.date_to = merged.date_to;

  // facetFilters → individual filters
  const facets = flattenFacetFilters(merged.facetFilters);
  const carTypes: string[] = [];
  const eras: string[] = [];
  const colourIds: string[] = [];
  const makes: string[] = [];
  const features: string[] = [];
  for (const f of facets) {
    const [k, v] = f.split(":");
    if (!k || v == null) continue;
    if (k === "rental_driving_active" && v === "true") params.rental_type = "driving";
    else if (k === "rental_chauffeured_active" && v === "true") params.rental_type = "chauffeured";
    else if (k === "rental_event_active" && v === "true") params.rental_type = "event";
    else if (k === "make") makes.push(v);
    else if (k === "model") params.model = v;
    else if (k === "car_types") carTypes.push(v);
    else if (k === "eras") eras.push(v);
    else if (k === "colour_id") colourIds.push(v);
    else if (k === "instant_book" && v === "true") features.push("instant");
    else if (k === "convertible" && v === "true") features.push("convertible");
    else if (k === "automatic" && v === "true") features.push("automatic");
    else if ((k === "air_con" || k === "has_aircon") && v === "true") features.push("air_con");
    else if (k === "country_code") params.country_code = v;
  }
  if (carTypes.length) params.car_type = carTypes;
  if (eras.length) params.era = eras;
  if (colourIds.length) params.colour_id = [...new Set(colourIds)];
  if (makes.length) params.make = [...new Set(makes)].join(",");
  if (features.length) params.features = features;

  // numericFilters → price range / passengers. The price slider can be bound
  // to one of four attributes depending on rental type — see task #1537. We
  // forward whichever attribute the client picked so `/cars/search` can apply
  // the matching column (or waterfall for `filter_price_per_day`).
  const PRICE_ATTRS = new Set([
    "filter_price_per_day",
    "price_driving",
    "price_event",
    "chauffeured_min_price_hourly_rate",
  ]);
  const numerics = flattenNumeric(merged.numericFilters);
  for (const n of numerics) {
    const m = n.match(/^([a-z_]+)\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)/i);
    if (!m) continue;
    const [, attr, op, raw] = m;
    const val = Number(raw);
    if (PRICE_ATTRS.has(attr)) {
      if (op === ">=" || op === ">") params.price_min = val;
      else if (op === "<=" || op === "<") params.price_max = val;
      params.price_attribute = attr as SearchParams["price_attribute"];
    } else if (attr === "passengers") {
      if (op === ">=" || op === ">") params.passengers = val;
    }
  }

  // `filters` string: passengers >= N, (city_lc:"x" OR state_lc:"x")
  const filters = typeof merged.filters === "string" ? merged.filters : "";
  if (filters) {
    const passengersMatch = filters.match(/passengers\s*>=\s*(\d+)/i);
    if (passengersMatch && params.passengers == null) params.passengers = Number(passengersMatch[1]);
    // Country browse/typed-country scope is configured through Algolia's
    // `filters` string, not facetFilters. Keep it when a request fails over
    // to the database; the US exclusion is a negation, not a country anchor.
    const positiveCountry = [...filters.matchAll(/\bcountry_code:([A-Z]{2})\b/gi)]
      .find(match => !/\bNOT\s*$/i.test(filters.slice(0, match.index)));
    if (positiveCountry && !params.country_code) params.country_code = positiveCountry[1].toUpperCase();
    const cityMatch = filters.match(/city_lc:"([^"]+)"/i);
    const stateMatch = filters.match(/state_lc:"([^"]+)"/i);
    if (!params.location && (cityMatch || stateMatch)) {
      params.location = (cityMatch?.[1] || stateMatch?.[1] || "").trim();
    }
  }

  // Geo bounds via insideBoundingBox take precedence over aroundLatLng,
  // mirroring the AlgoliaSearchPageContent behaviour: when the user pans
  // the map and clicks "Search this area" the viewport overrides any
  // typed-location radius. Algolia accepts either a `[[lat1,lng1,lat2,lng2], ...]`
  // array or a comma-joined string; we collapse whatever shape we got
  // into a single bounding rectangle for the Supabase fallback (the
  // backend already handles antimeridian wrap-around itself).
  const ibb = merged.insideBoundingBox;
  let polygons: number[][] = [];
  if (Array.isArray(ibb)) {
    polygons = (ibb as unknown[]).map(p =>
      Array.isArray(p) ? (p as unknown[]).map(n => Number(n)) : []
    ).filter(p => p.length === 4 && p.every(n => Number.isFinite(n)));
  } else if (typeof ibb === "string" && ibb.trim()) {
    const nums = ibb.split(",").map(s => Number(s.trim())).filter(n => Number.isFinite(n));
    for (let i = 0; i + 4 <= nums.length; i += 4) polygons.push(nums.slice(i, i + 4));
  }
  if (polygons.length > 0) {
    let north = -Infinity, south = Infinity;
    for (const [lat1, , lat2] of polygons) {
      north = Math.max(north, lat1, lat2);
      south = Math.min(south, lat1, lat2);
    }
    if (polygons.length === 1) {
      const [, lng1, , lng2] = polygons[0];
      params.bounds_north = north;
      params.bounds_south = south;
      params.bounds_east = Math.max(lng1, lng2);
      params.bounds_west = Math.min(lng1, lng2);
    } else {
      // Antimeridian wrap. The client splits a wrap-around viewport
      // into two polygons: one running from the user's `west` to +180,
      // and another from -180 to the user's `east`. We need to recover
      // those original `west` and `east` edges and emit `west > east`,
      // which is the convention `/api/cars/search` already handles.
      // Naively taking min/max of all lng values would collapse to
      // [-180, +180] (the whole world), which is the regression the
      // code review caught.
      let userWest = -Infinity; // most positive west edge
      let userEast = Infinity;  // most negative east edge
      for (const [, lng1, , lng2] of polygons) {
        const lngMin = Math.min(lng1, lng2);
        const lngMax = Math.max(lng1, lng2);
        if (lngMax >= 180) {
          // Polygon stretches to +180 → its lower lng is the user's west.
          userWest = Math.max(userWest, lngMin);
        } else if (lngMin <= -180) {
          // Polygon stretches to -180 → its upper lng is the user's east.
          userEast = Math.min(userEast, lngMax);
        }
      }
      if (Number.isFinite(userWest) && Number.isFinite(userEast)) {
        params.bounds_north = north;
        params.bounds_south = south;
        params.bounds_west = userWest;
        params.bounds_east = userEast;
      } else {
        // Fallback: if we can't identify the wrap edges, take a union.
        let east = -Infinity, west = Infinity;
        for (const [, lng1, , lng2] of polygons) {
          east = Math.max(east, lng1, lng2);
          west = Math.min(west, lng1, lng2);
        }
        params.bounds_north = north;
        params.bounds_south = south;
        params.bounds_east = east;
        params.bounds_west = west;
      }
    }
  }

  // Geo bounds via aroundLatLng + aroundRadius (meters). Skipped when
  // `insideBoundingBox` already established a viewport — bounds take
  // precedence over the typed-location radius search, mirroring the
  // primary Algolia path.
  const around = merged.aroundLatLng;
  if (polygons.length === 0 && typeof around === "string" && around.includes(",")) {
    const [latStr, lngStr] = around.split(",").map(s => s.trim());
    const lat = Number(latStr);
    const lng = Number(lngStr);
    const radiusMeters = Number(merged.aroundRadius);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(radiusMeters) && radiusMeters > 0) {
      // Preserve the circular radius, not its larger bounding rectangle.
      params.anchor_lat = lat;
      params.anchor_lng = lng;
      params.radius = radiusMeters / 1609.344;
    }
  }

  // Sort comes from indexName (replica). Default index → relevance
  const indexName = req.indexName || "";
  for (const key of Object.keys(SORT_INDEX_TO_KEY)) {
    if (indexName.endsWith(`_${key}`)) {
      params.sort = SORT_INDEX_TO_KEY[key];
      break;
    }
  }

  return params;
}

function carToAlgoliaHit(c: CarCardData): Record<string, unknown> {
  return {
    objectID: String(c.id),
    id: c.id,
    slug: c.slug,
    make: c.make,
    model: c.model,
    year: c.year,
    city: c.city,
    state: c.state,
    country_code: c.countryCode ?? null,
    price_driving: c.priceDriving,
    price_event: c.priceEvent,
    chauffeured_min_price_hourly_rate: c.chauffeurMinPrice,
    event_min_price_hourly_rate: c.eventMinPrice,
    event_hourly_rate: c.eventHourlyRate,
    chauffeured_hourly_rate: c.chauffeuredHourlyRate,
    minimum_price: c.minimumPrice,
    image: c.primaryImageUrl,
    image_thumbnail: c.primaryImageCdnThumbnail ?? null,
    image_medium: c.primaryImageCdnMedium ?? null,
    image_full: c.primaryImageCdnFull ?? null,
    rental_driving_active: c.rentalDrivingActive,
    rental_chauffeured_active: c.rentalChauffeurActive,
    rental_event_active: c.rentalEventActive,
    total_score: c.totalScore,
    reviews_rating: c.reviewsRating,
    reviews_count: c.reviewsCount,
    automatic: c.transmission?.toLowerCase() === "automatic",
    passengers: c.passengers,
    currency_code: c.currencyCode,
    _geoloc: c.latitude != null && c.longitude != null ? { lat: c.latitude, lng: c.longitude } : undefined,
    owner_pro_tier: c.ownerProTier ?? null,
    instant_book: c.instantBook ?? false,
  };
}

function resultToAlgoliaResponse(req: AlgoliaRequest, result: SearchResult, indexName: string) {
  const hitsPerPage = result.pageSize || (req.hitsPerPage as number) || 12;
  const page = Math.max((result.page || 1) - 1, 0);
  const nbPages = Math.max(Math.ceil(result.total / Math.max(hitsPerPage, 1)), result.cars.length > 0 ? 1 : 0);
  return {
    hits: result.cars.map(carToAlgoliaHit),
    nbHits: result.total,
    page,
    nbPages,
    hitsPerPage,
    processingTimeMS: 0,
    query: (typeof req.params === "object" && req.params ? (req.params as { query?: string }).query : "") || req.query || "",
    params: typeof req.params === "string" ? req.params : "",
    index: indexName,
    exhaustiveNbHits: true,
    facets: {},
  };
}

export async function runSupabaseFallback(requests: AlgoliaRequest[]): Promise<{ results: ReturnType<typeof resultToAlgoliaResponse>[] }> {
  const results = await Promise.all(
    requests.map(async (req) => {
      const indexName = req.indexName || "";
      try {
        const params = algoliaRequestToSearchParams(req);
        const result = await api.searchCars(params);
        return resultToAlgoliaResponse(req, result, indexName);
      } catch (e) {
        try { Sentry.captureException(e); } catch { /* noop */ }
        // A failed request is not a successful zero, and must not trigger
        // nearby recovery or claim there are no eligible cars.
        throw e;
      }
    }),
  );
  return { results };
}

function classifyError(err: unknown): { reason: FallbackReason; status: number | null } {
  const e = err as { status?: number; statusCode?: number; code?: string; message?: string };
  const status = (typeof e?.status === "number" ? e.status : typeof e?.statusCode === "number" ? e.statusCode : null);
  const msg = e?.message || "";
  if (e?.code === "ETIMEDOUT" || /timed? ?out|timeout/i.test(msg)) return { reason: "timeout", status };
  if (status === 429 || /rate ?limit|too many requests/i.test(msg)) return { reason: "rate_limited", status };
  return { reason: "error", status };
}

export function shouldFallback(err: unknown): boolean {
  const e = err as { status?: number; statusCode?: number; message?: string };
  const status = typeof e?.status === "number" ? e.status : typeof e?.statusCode === "number" ? e.statusCode : 0;
  const msg = e?.message || "";
  // Don't fall back for known "index not found" — provider already handles those.
  if (msg.includes("does not exist") || msg.includes("IndexNotFoundException")) return false;
  // Network errors, timeouts, server errors, rate limits → fallback.
  if (!status) return true;
  if (status === 408 || status === 429) return true;
  if (status >= 500) return true;
  return false;
}

export function reportFallbackTrigger(err: unknown) {
  const { reason, status } = classifyError(err);
  activateFallback(reason, err, status);
}
